import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type {
  CommercialSummary,
  EmploymentStatus,
  RepaymentPlanTemplate,
  SolarProduct,
} from '@solar/contracts';
import { API_ERROR_CODES, EMPLOYMENT_STATUSES, PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { SelectField } from '../../../components/forms/SelectField';
import { PageSpinner } from '../../../components/PageSpinner';
import { applicationsApi } from '../../../features/applications/applications.api';
import { useAuth } from '../../../features/auth/auth-context';
import { catalogueApi, filterPlansForProduct } from '../../../features/catalogue/catalogue.api';
import { customersApi } from '../../../features/customers/customers.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import heroHouseUrl from '../../../images/dashboard-house-hero.png';
import { ApiError } from '../../../lib/api-error';
import { calculateCommercialSummary } from '../../../lib/commercial-summary';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { paths, routeTo } from '../../../routes/paths';

import styles from './ApplicationWizardPage.module.css';

const STEPS = ['Product', 'Plan', 'Installation details', 'Summary'] as const;
const STEP_KEYS = ['product', 'plan', 'terms', 'summary'] as const;
type StepIndex = 0 | 1 | 2 | 3;

interface WizardLocationState {
  productId?: string;
}

interface ExistingJourneyRecovery {
  id: string;
  label: string;
  route: string;
}

function existingJourneyRecovery(error: unknown): ExistingJourneyRecovery | null {
  if (
    !(error instanceof ApiError) ||
    error.code !== API_ERROR_CODES.APPLICATION_ACTIVE_JOURNEY_EXISTS
  ) {
    return null;
  }

  const id = error.details.find((detail) => detail.field === 'existingJourneyId')?.value;
  const status = error.details.find((detail) => detail.field === 'existingJourneyStatus')?.value;

  if (id === undefined || status === undefined) return null;

  if (status === 'DRAFT') {
    return { id, label: 'Continue application', route: routeTo.customerApplicationEdit(id) };
  }

  if (status === 'CHANGES_REQUESTED') {
    return { id, label: 'Make requested changes', route: routeTo.customerApplicationEdit(id) };
  }

  const applicationStatuses = ['SUBMITTED', 'UNDER_REVIEW', 'REJECTED'];
  return applicationStatuses.includes(status)
    ? { id, label: 'View application', route: routeTo.customerApplication(id) }
    : { id, label: 'View financing journey', route: routeTo.customerContract(id) };
}

function parseStep(value: string | null): StepIndex {
  const index = STEP_KEYS.indexOf(value as (typeof STEP_KEYS)[number]);
  return index >= 0 ? (index as StepIndex) : 0;
}

function customerLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function planFrequency(plan: RepaymentPlanTemplate): string {
  return customerLabel(plan.repaymentFrequency);
}

function planSummary(
  product: SolarProduct,
  plan: RepaymentPlanTemplate,
): CommercialSummary | undefined {
  try {
    return calculateCommercialSummary({
      productPrice: product.basePrice,
      discountAmount: '0.00',
      depositType: plan.depositType,
      depositValue: plan.depositValue,
      tenureMonths: plan.tenureMonths,
    });
  } catch {
    return undefined;
  }
}

function ProductIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 13.5 12 5l9 8.5M5.5 11.5V20h13v-8.5M9 20v-5h6v5" />
      <path d="m9 8 3-5 3 5M10.5 5.5h3" />
    </svg>
  );
}

function CalendarIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 4.5h14a2 2 0 0 1 2 2V20H3V6.5a2 2 0 0 1 2-2ZM7 2v5M17 2v5M3 9h18" />
      <path d="M7 13h2M11 13h2M15 13h2M7 17h2M11 17h2" />
    </svg>
  );
}

function SelectionMark({ selected }: { readonly selected: boolean }): React.JSX.Element {
  return (
    <span
      className={selected ? styles.selectionMarkSelected : styles.selectionMark}
      aria-hidden="true"
    >
      {selected && <span />}
    </span>
  );
}

export function ApplicationWizardPage(): React.JSX.Element {
  const { can } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationProductId = (location.state as WizardLocationState | null)?.productId ?? '';
  const applicationId = searchParams.get('applicationId') ?? '';

  const [step, setStepState] = useState<StepIndex>(() => parseStep(searchParams.get('step')));
  const [productId, setProductId] = useState(
    () => searchParams.get('productId') ?? locationProductId,
  );
  const [planId, setPlanId] = useState(() => searchParams.get('planTemplateId') ?? '');
  const [installationAddressId, setInstallationAddressId] = useState(
    () => searchParams.get('installationAddressId') ?? '',
  );
  const [employmentStatus, setEmploymentStatus] = useState<EmploymentStatus | ''>(
    () => (searchParams.get('employmentStatus') as EmploymentStatus | null) ?? '',
  );
  const [showValidation, setShowValidation] = useState(false);
  const initializedApplicationId = useRef('');

  const updateProgressUrl = (updates: Record<string, string | null>): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('debitDay');
    next.delete('firstDueDate');
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
  };

  const setStep = (nextStep: StepIndex): void => {
    setShowValidation(false);
    setStepState(nextStep);
    updateProgressUrl({ step: STEP_KEYS[nextStep] });
  };

  useEffect(() => {
    if (searchParams.get('productId') === null && locationProductId !== '') {
      updateProgressUrl({ productId: locationProductId, step: STEP_KEYS[step] });
    }
    // This one-time bridge keeps older location-state links working, then the URL owns progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profile = useQuery({
    queryKey: ['customer', 'profile'],
    queryFn: () => customersApi.getProfile(),
    retry: false,
  });
  const addresses = useQuery({
    queryKey: ['customer', 'addresses'],
    queryFn: () => customersApi.listAddresses(),
    enabled: profile.isSuccess,
  });
  const products = useQuery({
    queryKey: ['catalogue', 'products', 'customer', 'application-wizard'],
    queryFn: () => catalogueApi.listCustomerProducts({ pageSize: 100 }),
  });
  const plans = useQuery({
    queryKey: ['catalogue', 'plans', 'active'],
    queryFn: () => catalogueApi.listCustomerRepaymentPlans({ status: 'ACTIVE', pageSize: 100 }),
  });
  const existingApplication = useQuery({
    queryKey: ['applications', applicationId],
    queryFn: () => applicationsApi.get(applicationId),
    enabled: applicationId !== '',
    retry: false,
  });

  useEffect(() => {
    const application = existingApplication.data;
    if (application === undefined || initializedApplicationId.current === application.id) return;

    initializedApplicationId.current = application.id;
    setProductId(application.productId);
    setPlanId(application.planTemplateId);
    setInstallationAddressId(application.installationAddressId ?? '');
    setEmploymentStatus(application.employmentStatus ?? '');

    if (application.status === 'DRAFT' || application.status === 'CHANGES_REQUESTED') {
      setStepState(2);
      updateProgressUrl({
        productId: application.productId,
        planTemplateId: application.planTemplateId,
        installationAddressId: application.installationAddressId,
        employmentStatus: application.employmentStatus,
        step: 'terms',
      });
    }
    // Hydrate only when a different saved application is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingApplication.data]);

  useEffect(() => {
    if (installationAddressId !== '' || addresses.data === undefined) return;

    const preferred =
      addresses.data.find((address) => address.addressType === 'INSTALLATION') ??
      addresses.data.find(
        (address) => address.addressType === 'RESIDENTIAL' && address.isDefault === 'true',
      ) ??
      addresses.data.find((address) => address.addressType === 'RESIDENTIAL');

    if (preferred !== undefined) {
      setInstallationAddressId(preferred.id);
      updateProgressUrl({ installationAddressId: preferred.id });
    }
    // Initialize once when address data becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses.data, installationAddressId]);

  const selectedProduct = products.data?.items.find((item) => item.id === productId);
  const eligiblePlans = useMemo(() => {
    if (selectedProduct === undefined || plans.data === undefined) return [];
    return filterPlansForProduct(plans.data.items, selectedProduct.basePrice);
  }, [plans.data, selectedProduct]);
  const selectedPlan = eligiblePlans.find((item) => item.id === planId);
  const selectedInstallationAddress = addresses.data?.find(
    (address) => address.id === installationAddressId,
  );
  const selectedCommercial = useMemo(
    () =>
      selectedProduct === undefined || selectedPlan === undefined
        ? undefined
        : planSummary(selectedProduct, selectedPlan),
    [selectedPlan, selectedProduct],
  );

  useEffect(() => {
    if (applicationId !== '' || !products.isSuccess || !plans.isSuccess) return;
    if (step > 0 && selectedProduct === undefined) {
      setStepState(0);
      updateProgressUrl({ step: 'product', planTemplateId: null });
    } else if (step > 1 && selectedPlan === undefined) {
      setStepState(1);
      updateProgressUrl({ step: 'plan' });
    }
    // Only repair externally edited or incomplete deep links after catalogue data is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, plans.isSuccess, products.isSuccess, selectedPlan, selectedProduct, step]);

  const installationAddressError =
    installationAddressId === '' ? 'Select the installation or delivery address.' : undefined;
  const termsAreValid = installationAddressError === undefined;
  const isEditing = applicationId !== '';
  const editableExistingApplication =
    !isEditing ||
    existingApplication.data?.status === 'DRAFT' ||
    existingApplication.data?.status === 'CHANGES_REQUESTED';
  const selectionLocked = existingApplication.data?.status === 'CHANGES_REQUESTED';

  const save = useMutation({
    mutationFn: () => {
      if (selectedProduct === undefined || selectedPlan === undefined || !termsAreValid) {
        throw new Error('Review the highlighted application details and try again.');
      }

      const terms = {
        installationAddressId,
        ...(employmentStatus === '' ? {} : { employmentStatus }),
      };

      return isEditing
        ? applicationsApi.update(applicationId, {
            ...(existingApplication.data?.status === 'DRAFT'
              ? { productId: selectedProduct.id, planTemplateId: selectedPlan.id }
              : {}),
            ...terms,
          })
        : applicationsApi.createForCustomer({
            productId: selectedProduct.id,
            planTemplateId: selectedPlan.id,
            ...terms,
          });
    },
    onSuccess: async (application) => {
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
      void navigate(routeTo.customerApplication(application.id), { state: { draftSaved: true } });
    },
  });

  const canCreate = can(PERMISSIONS.SUBSCRIPTIONS_CREATE) && profile.isSuccess;
  const canUpdate = can(PERMISSIONS.SUBSCRIPTIONS_UPDATE);
  const canSave = isEditing ? canUpdate : canCreate;
  const journeyRecovery = existingJourneyRecovery(save.error);

  if (isEditing && existingApplication.isPending) {
    return (
      <div className={styles.page}>
        <PageSpinner label="Loading saved application" />
      </div>
    );
  }

  if (isEditing && existingApplication.isError) {
    return (
      <div className={styles.page}>
        <Alert tone="error">
          We couldn&apos;t load this application. Please return to your applications and try again.
        </Alert>
        <Link className={styles.textLink} to={paths.customerApplications}>
          Back to applications
        </Link>
      </div>
    );
  }

  if (!editableExistingApplication && existingApplication.data !== undefined) {
    return (
      <div className={styles.page}>
        <Alert tone="info">
          This application can no longer be edited because it has already been submitted.
        </Alert>
        <Link
          className={styles.textLink}
          to={routeTo.customerApplication(existingApplication.data.id)}
        >
          View application
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Link
        className={styles.backLink}
        to={isEditing ? routeTo.customerApplication(applicationId) : paths.customerApplications}
      >
        <span aria-hidden="true">←</span>{' '}
        {isEditing ? 'Back to application' : 'Back to applications'}
      </Link>

      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>{isEditing ? 'Continue application' : 'Apply for solar'}</p>
          <h1 className={styles.pageTitle}>Application wizard</h1>
        </div>
        <p className={styles.pageSubtitle}>
          Choose a product and plan, then review your installation details.
        </p>
      </header>

      {existingApplication.data?.status === 'CHANGES_REQUESTED' &&
        existingApplication.data.changeRequestReason !== null && (
          <Alert tone="info">
            Changes requested: {existingApplication.data.changeRequestReason}
          </Alert>
        )}

      <ol className={styles.stepper} aria-label="Application progress">
        {STEPS.map((label, index) => {
          const completed = index < step;
          const current = index === step;
          const reachable = index <= step && !(selectionLocked && index < 2);
          return (
            <li
              key={label}
              className={
                completed
                  ? styles.stepCompleted
                  : current
                    ? styles.stepCurrent
                    : styles.stepUpcoming
              }
            >
              <button
                type="button"
                className={styles.stepButton}
                disabled={!reachable}
                aria-current={current ? 'step' : undefined}
                onClick={() => setStep(index as StepIndex)}
              >
                <span className={styles.stepCircle}>{completed ? '✓' : index + 1}</span>
                <span className={styles.stepLabel}>{label}</span>
              </button>
              {index < STEPS.length - 1 && (
                <span className={styles.stepConnector} aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>

      <div className={styles.wizardShell}>
        <main className={styles.stage}>
          {step === 0 && (
            <section aria-labelledby="step-product-heading">
              <div className={styles.sectionHeading}>
                <h2 id="step-product-heading">Select your solar product</h2>
                <p>Choose the available system that best matches your needs.</p>
              </div>

              {selectionLocked && (
                <Alert tone="info">
                  The product is fixed for this saved application. You can review it here and
                  continue to installation details.
                </Alert>
              )}
              {products.isPending && <PageSpinner label="Loading products" />}
              {products.isError && (
                <div className={styles.statePanel}>
                  <Alert tone="error">We couldn&apos;t load the available products.</Alert>
                  <Button busy={products.isFetching} onClick={() => void products.refetch()}>
                    Retry
                  </Button>
                </div>
              )}
              {products.isSuccess && products.data.items.length === 0 && (
                <div className={styles.statePanel}>
                  <h3>No solar products are available right now</h3>
                  <p>Please check back later or return to the product catalogue.</p>
                  <Link className={styles.textLink} to={paths.customerProducts}>
                    View catalogue
                  </Link>
                </div>
              )}
              {products.isSuccess && products.data.items.length > 0 && (
                <div className={styles.optionList} role="radiogroup" aria-label="Solar products">
                  {products.data.items.map((product) => {
                    const selected = product.id === productId;
                    return (
                      <label
                        className={selected ? styles.productCardSelected : styles.productCard}
                        key={product.id}
                      >
                        <input
                          className={styles.nativeRadio}
                          type="radio"
                          name="product"
                          value={product.id}
                          checked={selected}
                          disabled={selectionLocked}
                          onChange={() => {
                            setProductId(product.id);
                            setPlanId('');
                            updateProgressUrl({ productId: product.id, planTemplateId: null });
                          }}
                        />
                        <span className={styles.productGlyph}>
                          <ProductIcon />
                        </span>
                        <span className={styles.optionCopy}>
                          <span className={styles.optionTopline}>
                            <strong>{product.name}</strong>
                            <span className={styles.optionPrice}>
                              {formatNgn(product.basePrice)}
                            </span>
                          </span>
                          <span className={styles.optionDescription}>{product.description}</span>
                          <span className={styles.productFacts}>
                            <span>{product.systemCapacityKw} kW</span>
                            <span>{customerLabel(product.category)}</span>
                            {product.warrantySummary !== null &&
                              product.warrantySummary.trim() !== '' && (
                                <span>{product.warrantySummary}</span>
                              )}
                          </span>
                        </span>
                        <SelectionMark selected={selected} />
                      </label>
                    );
                  })}
                </div>
              )}

              <div className={styles.actionsEnd}>
                <Button
                  className={styles.primaryAction}
                  disabled={selectedProduct === undefined}
                  onClick={() => setStep(1)}
                >
                  Next <span aria-hidden="true">→</span>
                </Button>
              </div>
            </section>
          )}

          {step === 1 && (
            <section aria-labelledby="step-plan-heading">
              <div className={styles.sectionHeading}>
                <h2 id="step-plan-heading">Select a repayment plan</h2>
                <p>Compare the active plans available for your selected product.</p>
              </div>

              {selectionLocked && (
                <Alert tone="info">
                  The repayment plan is fixed for this saved application. You can review it here and
                  continue.
                </Alert>
              )}
              {plans.isPending && <PageSpinner label="Loading repayment plans" />}
              {plans.isError && (
                <div className={styles.statePanel}>
                  <Alert tone="error">We couldn&apos;t load repayment plans.</Alert>
                  <Button busy={plans.isFetching} onClick={() => void plans.refetch()}>
                    Retry
                  </Button>
                </div>
              )}
              {plans.isSuccess && eligiblePlans.length === 0 && (
                <div className={styles.statePanel}>
                  <h3>No repayment plans are available for this product</h3>
                  <p>Choose a different product or check back later.</p>
                  {!selectionLocked && (
                    <Button variant="secondary" onClick={() => setStep(0)}>
                      Choose another product
                    </Button>
                  )}
                </div>
              )}
              {plans.isSuccess && eligiblePlans.length > 0 && selectedProduct !== undefined && (
                <div className={styles.optionList} role="radiogroup" aria-label="Repayment plans">
                  {eligiblePlans.map((plan) => {
                    const selected = plan.id === planId;
                    const commercial = planSummary(selectedProduct, plan);
                    return (
                      <label
                        className={selected ? styles.planCardSelected : styles.planCard}
                        key={plan.id}
                      >
                        <input
                          className={styles.nativeRadio}
                          type="radio"
                          name="plan"
                          value={plan.id}
                          checked={selected}
                          disabled={selectionLocked}
                          onChange={() => {
                            setPlanId(plan.id);
                            updateProgressUrl({ planTemplateId: plan.id });
                          }}
                        />
                        <span className={styles.planGlyph}>
                          <CalendarIcon />
                        </span>
                        <span className={styles.optionCopy}>
                          <span className={styles.optionTopline}>
                            <strong>{plan.name}</strong>
                            <span className={styles.planDuration}>{plan.tenureMonths} months</span>
                          </span>
                          {plan.description !== null && plan.description.trim() !== '' && (
                            <span className={styles.optionDescription}>{plan.description}</span>
                          )}
                          {commercial !== undefined && (
                            <span className={styles.planFacts}>
                              <span>
                                <small>{planFrequency(plan)} instalment</small>
                                <strong>{formatNgn(commercial.instalmentAmount)}</strong>
                              </span>
                              <span>
                                <small>Deposit</small>
                                <strong>{formatNgn(commercial.depositAmount)}</strong>
                              </span>
                              <span>
                                <small>Total repayable</small>
                                <strong>{formatNgn(commercial.totalRepayableAmount)}</strong>
                              </span>
                            </span>
                          )}
                        </span>
                        <SelectionMark selected={selected} />
                      </label>
                    );
                  })}
                </div>
              )}

              <div className={styles.actionsSplit}>
                <Button
                  className={styles.secondaryAction}
                  variant="secondary"
                  disabled={selectionLocked}
                  onClick={() => setStep(0)}
                >
                  ← Back
                </Button>
                <Button
                  className={styles.primaryAction}
                  disabled={selectedPlan === undefined}
                  onClick={() => setStep(2)}
                >
                  Next <span aria-hidden="true">→</span>
                </Button>
              </div>
            </section>
          )}

          {step === 2 && (
            <section aria-labelledby="step-terms-heading">
              <div className={styles.sectionHeading}>
                <h2 id="step-terms-heading">Confirm installation details</h2>
                <p>Choose the installation location for your solar system.</p>
              </div>

              <div className={styles.termsIntro}>
                <span className={styles.termsIcon}>
                  <CalendarIcon />
                </span>
                <div>
                  <strong>Repayment schedule after review</strong>
                  <p>
                    Admin establishes the first repayment date on a supported day from 1 to 28. You
                    will review the approved schedule before accepting the contract.
                  </p>
                </div>
              </div>

              <div className={styles.termsGrid}>
                <div className={styles.field}>
                  <SelectField
                    label="Installation / delivery address"
                    name="installationAddressId"
                    required
                    value={installationAddressId}
                    aria-invalid={
                      showValidation && installationAddressError !== undefined ? true : undefined
                    }
                    onChange={(event) => {
                      setInstallationAddressId(event.target.value);
                      updateProgressUrl({ installationAddressId: event.target.value });
                    }}
                  >
                    <option value="">Select an address</option>
                    {(addresses.data ?? []).map((address) => (
                      <option key={address.id} value={address.id}>
                        {customerLabel(address.addressType)} — {address.addressLine1},{' '}
                        {address.city}
                      </option>
                    ))}
                  </SelectField>
                  <p>
                    Choose where this solar system will be installed. Selecting your residential
                    address confirms it is also the installation location.
                  </p>
                  <Link className={styles.textLink} to={paths.customerAddresses}>
                    Add or update an installation address
                  </Link>
                  {addresses.isError && (
                    <span className={styles.fieldError}>
                      We could not load your saved addresses.
                    </span>
                  )}
                  {showValidation && installationAddressError !== undefined && (
                    <span className={styles.fieldError}>{installationAddressError}</span>
                  )}
                </div>

                <div className={styles.field}>
                  <SelectField
                    label="Employment status (optional)"
                    name="employmentStatus"
                    value={employmentStatus}
                    onChange={(event) => {
                      const value = event.target.value as EmploymentStatus | '';
                      setEmploymentStatus(value);
                      updateProgressUrl({ employmentStatus: value });
                    }}
                  >
                    <option value="">Not provided</option>
                    {EMPLOYMENT_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {customerLabel(status)}
                      </option>
                    ))}
                  </SelectField>
                  <p>
                    This records application context only and is not used for automated scoring.
                  </p>
                </div>
              </div>

              <div className={styles.actionsSplit}>
                <Button
                  className={styles.secondaryAction}
                  variant="secondary"
                  disabled={selectionLocked}
                  onClick={() => setStep(1)}
                >
                  ← Back
                </Button>
                <Button
                  className={styles.primaryAction}
                  onClick={() => {
                    setShowValidation(true);
                    if (termsAreValid) setStep(3);
                  }}
                >
                  Review application <span aria-hidden="true">→</span>
                </Button>
              </div>
            </section>
          )}

          {step === 3 &&
            selectedProduct !== undefined &&
            selectedPlan !== undefined &&
            selectedCommercial !== undefined && (
              <section aria-labelledby="step-summary-heading">
                <div className={styles.sectionHeading}>
                  <h2 id="step-summary-heading">Review your application</h2>
                  <p>Check each section before saving your draft application.</p>
                </div>

                <div className={styles.reviewList}>
                  <section
                    className={styles.reviewSection}
                    aria-labelledby="review-product-heading"
                  >
                    <div className={styles.reviewHeader}>
                      <div>
                        <span>Solar product</span>
                        <h3 id="review-product-heading">{selectedProduct.name}</h3>
                      </div>
                      {!selectionLocked && (
                        <button type="button" onClick={() => setStep(0)}>
                          Edit
                        </button>
                      )}
                    </div>
                    <dl>
                      <div>
                        <dt>Capacity</dt>
                        <dd>{selectedProduct.systemCapacityKw} kW</dd>
                      </div>
                      <div>
                        <dt>Category</dt>
                        <dd>{customerLabel(selectedProduct.category)}</dd>
                      </div>
                      <div>
                        <dt>Base price</dt>
                        <dd>{formatNgn(selectedProduct.basePrice)}</dd>
                      </div>
                    </dl>
                  </section>
                  <section className={styles.reviewSection} aria-labelledby="review-plan-heading">
                    <div className={styles.reviewHeader}>
                      <div>
                        <span>Repayment plan</span>
                        <h3 id="review-plan-heading">{selectedPlan.name}</h3>
                      </div>
                      {!selectionLocked && (
                        <button type="button" onClick={() => setStep(1)}>
                          Edit
                        </button>
                      )}
                    </div>
                    <dl>
                      <div>
                        <dt>Duration</dt>
                        <dd>{selectedPlan.tenureMonths} months</dd>
                      </div>
                      <div>
                        <dt>{planFrequency(selectedPlan)} instalment</dt>
                        <dd>{formatNgn(selectedCommercial.instalmentAmount)}</dd>
                      </div>
                      <div>
                        <dt>Deposit</dt>
                        <dd>{formatNgn(selectedCommercial.depositAmount)}</dd>
                      </div>
                      <div>
                        <dt>Amount financed</dt>
                        <dd>{formatNgn(selectedCommercial.financedAmount)}</dd>
                      </div>
                      <div>
                        <dt>Total repayable</dt>
                        <dd>{formatNgn(selectedCommercial.totalRepayableAmount)}</dd>
                      </div>
                    </dl>
                  </section>
                  <section className={styles.reviewSection} aria-labelledby="review-terms-heading">
                    <div className={styles.reviewHeader}>
                      <div>
                        <span>Installation details</span>
                        <h3 id="review-terms-heading">Application details</h3>
                      </div>
                      <button type="button" onClick={() => setStep(2)}>
                        Edit
                      </button>
                    </div>
                    <dl>
                      <div>
                        <dt>Installation / delivery address</dt>
                        <dd>
                          {selectedInstallationAddress === undefined
                            ? 'Not selected'
                            : `${selectedInstallationAddress.addressLine1}, ${selectedInstallationAddress.city}, ${selectedInstallationAddress.stateCode}`}
                        </dd>
                      </div>
                      <div>
                        <dt>Employment status</dt>
                        <dd>
                          {employmentStatus === ''
                            ? 'Not provided'
                            : customerLabel(employmentStatus)}
                        </dd>
                      </div>
                    </dl>
                  </section>
                </div>

                {save.isError && journeyRecovery === null && (
                  <Alert tone="error">{toDisplayMessage(save.error)}</Alert>
                )}
                {journeyRecovery !== null && (
                  <Alert tone="info">
                    You already have a financing journey in progress.{' '}
                    <Link to={journeyRecovery.route}>{journeyRecovery.label}</Link>
                  </Alert>
                )}
                {!canSave && (
                  <Alert tone="info">
                    Your account cannot save an application yet.{' '}
                    <Link to={paths.customerOnboarding}>Review onboarding</Link>
                  </Alert>
                )}
                <div className={styles.draftNotice}>
                  <strong>This action saves a draft.</strong>
                  <span>
                    You will review and submit it for assessment from the application details page.
                  </span>
                </div>
                <div className={styles.actionsSplit}>
                  <Button
                    className={styles.secondaryAction}
                    variant="secondary"
                    onClick={() => setStep(2)}
                  >
                    ← Back
                  </Button>
                  {canSave && (
                    <Button
                      className={styles.primaryAction}
                      busy={save.isPending}
                      onClick={() => save.mutate()}
                    >
                      {isEditing ? 'Save draft changes' : 'Create draft application'}
                    </Button>
                  )}
                </div>
              </section>
            )}
        </main>

        <aside className={styles.summaryPanel} aria-labelledby="selection-summary-heading">
          <div className={styles.summaryVisual}>
            <img src={heroHouseUrl} alt="" />
            <span>Illustrative solar home</span>
          </div>
          <div className={styles.summaryBody}>
            <div className={styles.summaryHeading}>
              <p>Your selection</p>
              <h2 id="selection-summary-heading">
                {selectedProduct?.name ?? 'Build your application'}
              </h2>
            </div>
            {selectedProduct === undefined ? (
              <p className={styles.summaryEmpty}>
                Select a product to see its details and available repayment terms here.
              </p>
            ) : (
              <>
                <dl className={styles.summaryDetails}>
                  <div>
                    <dt>Capacity</dt>
                    <dd>{selectedProduct.systemCapacityKw} kW</dd>
                  </div>
                  <div>
                    <dt>Category</dt>
                    <dd>{customerLabel(selectedProduct.category)}</dd>
                  </div>
                  <div>
                    <dt>Base price</dt>
                    <dd>{formatNgn(selectedProduct.basePrice)}</dd>
                  </div>
                </dl>
                {selectedPlan !== undefined && selectedCommercial !== undefined && (
                  <section
                    className={styles.summarySection}
                    aria-labelledby="selected-plan-heading"
                  >
                    <span>Selected plan</span>
                    <h3 id="selected-plan-heading">{selectedPlan.name}</h3>
                    <dl className={styles.summaryDetails}>
                      <div>
                        <dt>Duration</dt>
                        <dd>{selectedPlan.tenureMonths} months</dd>
                      </div>
                      <div>
                        <dt>Deposit</dt>
                        <dd>{formatNgn(selectedCommercial.depositAmount)}</dd>
                      </div>
                      <div>
                        <dt>Amount financed</dt>
                        <dd>{formatNgn(selectedCommercial.financedAmount)}</dd>
                      </div>
                      <div>
                        <dt>{planFrequency(selectedPlan)} instalment</dt>
                        <dd>{formatNgn(selectedCommercial.instalmentAmount)}</dd>
                      </div>
                      <div>
                        <dt>Total repayable</dt>
                        <dd>{formatNgn(selectedCommercial.totalRepayableAmount)}</dd>
                      </div>
                    </dl>
                  </section>
                )}
                {step >= 2 && termsAreValid && (
                  <section
                    className={styles.summarySection}
                    aria-labelledby="selected-preferences-heading"
                  >
                    <span>Repayment schedule</span>
                    <h3 id="selected-preferences-heading">Established during Admin review</h3>
                    <p>
                      You will see the first repayment date and full schedule before contract
                      acceptance.
                    </p>
                  </section>
                )}
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
